import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import AttorneyBios from './components/AttorneyBios';
import Testimonials from './components/Testimonials';
import ImmigrantResources from './components/ImmigrantResources';
import ContactForm from './components/ContactForm';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SEOHead from '../../components/base/SEOHead';

const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${(import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com'}/#webpage`,
      url: (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com',
      name: 'Fishers Personal Injury Lawyer | Law Office of Hong-min Jun, P.C.',
      description:
        'Experienced personal injury and immigration attorney serving foreign-born individuals in Fishers, Indiana. Free consultation. Call (317) 701-2768.',
      isPartOf: {
        '@id': `${(import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com'}/#website`,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com',
          },
        ],
      },
    },
    {
      '@type': ['Attorney', 'LocalBusiness'],
      '@id': `${(import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com'}/#organization`,
      name: 'Law Office of Hong-min Jun, P.C.',
      url: (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com',
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 39.9568,
        longitude: -86.0139,
      },
      areaServed: {
        '@type': 'State',
        name: 'Indiana',
      },
      description:
        'Experienced personal injury and immigration attorney serving foreign-born individuals in Fishers, Indiana. Specializing in car accidents, workplace injuries, medical malpractice, slip and fall, wrongful death, dog bites, and immigration law.',
      priceRange: 'Free consultation',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '17:00',
        },
      ],
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Legal Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Car Accident Attorney' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Workplace Injury Attorney' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Medical Malpractice Attorney' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Slip and Fall Attorney' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Wrongful Death Attorney' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dog Bite Attorney' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Immigration Attorney' } },
        ],
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${(import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com'}/#website`,
      url: (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://junlawfirm.com',
      name: 'Law Office of Hong-min Jun, P.C.',
    },
  ],
};

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add scroll animation class to elements
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      section.classList.add('opacity-0');
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Fishers Personal Injury Lawyer | Law Office of Hong-min Jun, P.C."
        description="Experienced personal injury attorney serving foreign-born individuals in Fishers, Indiana. Specializing in car accidents, workplace injuries, and medical malpractice. Free consultation available. Call (317) 701-2768 for multilingual legal support."
        keywords="Fishers personal injury lawyer, Indiana accident attorney, immigrant legal services, car accident lawyer Fishers, workplace injury attorney, medical malpractice lawyer"
        canonicalPath="/"
        schema={homeSchema as Record<string, unknown>}
      />
      <Navbar />
      <Hero />
      <Services />
      <AttorneyBios />
      <Testimonials />
      <ImmigrantResources />
      <ContactForm />
      <Footer />
    </div>
  );
}
